import {
    BookOpen,
    Brain,
    FlaskConical,
    MessageSquare,
    Plus,
    Search,
    TrendingUp,
    Users,
    Zap
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import { API_URL } from '../config/api'
import { useAuth } from '../contexts/AuthContext'

interface ResearchProject {
  id: number
  title: string
  description: string
  category: string
  status: 'active' | 'paused' | 'completed' | 'planning'
  project_type: string
  budget_flz: number
  spent_flz: number
  total_findings: number
  total_questions: number
  total_contributions: number
  total_participants: number
  agent_count: number
  task_count: number
  owner_name: string
  created_at: string
}

const CATEGORY_COLORS: Record<string, string> = {
  Physics: '#6366f1',
  Chemistry: '#10b981',
  Biology: '#f59e0b',
  'AI/ML': '#3b82f6',
  Mathematics: '#8b5cf6',
  Engineering: '#ef4444',
  Astronomy: '#06b6d4',
  Neuroscience: '#ec4899',
  'Climate Science': '#84cc16',
  'Data Science': '#f97316',
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  Physics: <Zap size={14} />,
  Chemistry: <FlaskConical size={14} />,
  Biology: <Brain size={14} />,
  'AI/ML': <Brain size={14} />,
  Mathematics: <TrendingUp size={14} />,
  Astronomy: <Zap size={14} />,
}

function getCategoryColor(cat: string) {
  return CATEGORY_COLORS[cat] || '#6366f1'
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; color: string }> = {
    active: { label: 'Active', color: '#10b981' },
    paused: { label: 'Paused', color: '#f59e0b' },
    completed: { label: 'Completed', color: '#3b82f6' },
    planning: { label: 'Planning', color: '#8b5cf6' },
  }
  const s = map[status] || { label: status, color: '#6b7280' }
  return (
    <span style={{
      background: s.color + '22',
      color: s.color,
      border: `1px solid ${s.color}44`,
      padding: '2px 10px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.03em'
    }}>
      {s.label}
    </span>
  )
}

function ResearchCard({ project, onClick }: { project: ResearchProject; onClick: () => void }) {
  const catColor = getCategoryColor(project.category)
  const budgetUsed = project.budget_flz > 0
    ? Math.min(100, Math.round((project.spent_flz / project.budget_flz) * 100))
    : 0

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--border-color)',
        borderRadius: 16,
        padding: 24,
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={e => {
        const el = e.currentTarget as HTMLDivElement
        el.style.borderColor = catColor
        el.style.transform = 'translateY(-3px)'
        el.style.boxShadow = `0 8px 24px ${catColor}22`
      }}
      onMouseLeave={e => {
        const el = e.currentTarget as HTMLDivElement
        el.style.borderColor = 'var(--border-color)'
        el.style.transform = 'translateY(0)'
        el.style.boxShadow = 'none'
      }}
    >
      {/* Top accent line */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: 3, background: catColor, borderRadius: '16px 16px 0 0'
      }} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          {project.category && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: catColor + '18', color: catColor,
              padding: '3px 10px', borderRadius: 20, fontSize: 11,
              fontWeight: 600, marginBottom: 8
            }}>
              {CATEGORY_ICONS[project.category] || <FlaskConical size={12} />}
              {project.category}
            </div>
          )}
          <h3 style={{
            margin: 0, color: 'var(--text-primary)',
            fontSize: 15, fontWeight: 600, lineHeight: 1.4
          }}>
            {project.title}
          </h3>
        </div>
        <StatusBadge status={project.status} />
      </div>

      {/* Description */}
      {project.description && (
        <p style={{
          margin: 0, color: 'var(--text-secondary)',
          fontSize: 13, lineHeight: 1.6,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {project.description}
        </p>
      )}

      {/* Budget bar */}
      {project.budget_flz > 0 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Budget</span>
            <span style={{ fontSize: 11, color: catColor, fontWeight: 600 }}>
              {project.spent_flz.toLocaleString()} / {project.budget_flz.toLocaleString()} FLZ
            </span>
          </div>
          <div style={{
            height: 4, background: 'var(--border-color)',
            borderRadius: 4, overflow: 'hidden'
          }}>
            <div style={{
              height: '100%', width: `${budgetUsed}%`,
              background: catColor, borderRadius: 4,
              transition: 'width 0.3s'
            }} />
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 8, paddingTop: 12,
        borderTop: '1px solid var(--border-color)'
      }}>
        {[
          { icon: <BookOpen size={13} />, value: project.total_findings || 0, label: 'Findings' },
          { icon: <MessageSquare size={13} />, value: project.total_questions || 0, label: 'Questions' },
          { icon: <Users size={13} />, value: project.agent_count || 0, label: 'Agents' },
          { icon: <TrendingUp size={13} />, value: project.total_contributions || 0, label: 'Contribs' },
        ].map(s => (
          <div key={s.label} style={{ textAlign: 'center' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 3, color: catColor, marginBottom: 2
            }}>
              {s.icon}
              <span style={{ fontSize: 15, fontWeight: 700 }}>{s.value}</span>
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function Research() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [projects, setProjects] = useState<ResearchProject[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterCategory, setFilterCategory] = useState<string>('all')

  useEffect(() => {
    void loadProjects()
  }, [])

  const loadProjects = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await fetch(`${API_URL}/api/discover/projects?project_type=research`)
      if (!res.ok) {throw new Error('Failed to load')}
      const data = await res.json() as { items: ResearchProject[] }
      setProjects(data.items || [])
    } catch {
      setError('Research projeleri yüklenemedi')
    } finally {
      setLoading(false)
    }
  }

  const categories = ['all', ...Array.from(new Set(projects.map(p => p.category).filter(Boolean)))]

  const filtered = projects.filter(p => {
    const matchSearch = !search ||
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      (p.description || '').toLowerCase().includes(search.toLowerCase())
    const matchStatus = filterStatus === 'all' || p.status === filterStatus
    const matchCat = filterCategory === 'all' || p.category === filterCategory
    return matchSearch && matchStatus && matchCat
  })

  const totalFindings = projects.reduce((s, p) => s + (p.total_findings || 0), 0)
  const totalAgents = projects.reduce((s, p) => s + (p.agent_count || 0), 0)
  const activeCount = projects.filter(p => p.status === 'active').length

  return (
    <MainLayout>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
          <div>
            <h1 style={{ margin: '0 0 6px', color: 'var(--text-primary)', fontSize: 28, fontWeight: 700 }}>
              Research Projects
            </h1>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 14 }}>
              AI-powered continuous research — agents discover, analyze, and document
            </p>
          </div>
          {user && (
            <button
              onClick={() => navigate('/projects/create')}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'var(--primary-color)', color: '#fff',
                border: 'none', borderRadius: 10, padding: '10px 18px',
                fontSize: 14, fontWeight: 600, cursor: 'pointer'
              }}
            >
              <Plus size={16} /> New Research
            </button>
          )}
        </div>

        {/* Platform stats */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 16, marginBottom: 28
        }}>
          {[
            { label: 'Active Projects', value: activeCount, icon: <FlaskConical size={18} />, color: '#10b981' },
            { label: 'Total Findings', value: totalFindings, icon: <BookOpen size={18} />, color: '#6366f1' },
            { label: 'Active Agents', value: totalAgents, icon: <Brain size={18} />, color: '#f59e0b' },
          ].map(s => (
            <div key={s.label} style={{
              background: 'var(--card-bg)', border: '1px solid var(--border-color)',
              borderRadius: 12, padding: '16px 20px',
              display: 'flex', alignItems: 'center', gap: 14
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: s.color + '18', color: s.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                {s.icon}
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {s.value.toLocaleString()}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={16} style={{
              position: 'absolute', left: 14, top: '50%',
              transform: 'translateY(-50%)', color: 'var(--text-secondary)'
            }} />
            <input
              type="text"
              placeholder="Search research..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px 10px 40px',
                background: 'var(--card-bg)', border: '1px solid var(--border-color)',
                borderRadius: 10, color: 'var(--text-primary)', fontSize: 14,
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={{
              padding: '10px 14px', background: 'var(--card-bg)',
              border: '1px solid var(--border-color)', borderRadius: 10,
              color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer'
            }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
          </select>

          {/* Category filter */}
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            style={{
              padding: '10px 14px', background: 'var(--card-bg)',
              border: '1px solid var(--border-color)', borderRadius: 10,
              color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer'
            }}
          >
            {categories.map(c => (
              <option key={c} value={c}>{c === 'all' ? 'All Categories' : c}</option>
            ))}
          </select>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
            Loading research projects...
          </div>
        ) : error ? (
          <div style={{
            textAlign: 'center', padding: '60px 0',
            color: '#ef4444', background: '#ef444411',
            borderRadius: 12, border: '1px solid #ef444433'
          }}>
            {error}
            <br />
            <button
              onClick={() => void loadProjects()}
              style={{
                marginTop: 12, padding: '8px 16px',
                background: 'var(--primary-color)', color: '#fff',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13
              }}
            >
              Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '80px 20px',
            color: 'var(--text-secondary)'
          }}>
            <FlaskConical size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
            <p style={{ margin: 0, fontSize: 16 }}>
              {search || filterStatus !== 'all' || filterCategory !== 'all'
                ? 'No research projects match your filters'
                : 'No research projects yet'}
            </p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: 13 }}>
              {filtered.length} project{filtered.length !== 1 ? 's' : ''}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
              gap: 20
            }}>
              {filtered.map(p => (
                <ResearchCard
                  key={p.id}
                  project={p}
                  onClick={() => navigate(`/research/${p.id}`)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </MainLayout>
  )
}
